import React, { useState } from "react";
import {
  Grid,
  ButtonBase,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const CollapsableCard = (props) => {
  const {
    defaultExpanded = false,
    expanded: controlledExpanded,
    title,
    children,
    supertitle,
    cta,
    handleExpand,
    id,
    headerChipText,
    headerChipState,
    customArrowColor,
    customArrowColorDark,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* local control overwritten by passed in arguments */
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded || false);

  const defaultHandleExpand = () => setLocalExpanded((e) => !e);

  const expanded = controlledExpanded || localExpanded;

  const getHeaderChipColors = () => {
    switch (headerChipState) {
      case "gray":
        return {
          backgroundColor: theme.branding.gray[200],
          color: theme.branding.gray[600],
        };
      case "warning":
        return {
          backgroundColor: theme.branding.orange[50],
          color: theme.branding.yellow[700],
        };
      case "success":
        return {
          backgroundColor: theme.branding.green[100],
          color: theme.branding.green[700],
        };
      default:
        return {
          backgroundColor: theme.branding.gray[300],
          color: theme.branding.gray[700],
        };
    }
  };

  return (
    <Grid
      id={id}
      container
      direction="column"
      sx={[
        {
          border: `1px solid ${theme.branding.gray[200]}`,
          borderRadius: "12px",
          backgroundColor: theme.branding.gray[0],
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        },
        (theme) =>
          theme.applyStyles({
            borderColor: theme.branding.darkBlue[600],
            backgroundColor: theme.branding.darkBlue[800],
          }),
      ]}
    >
      <ButtonBase
        disableRipple
        onClick={handleExpand || defaultHandleExpand}
        sx={[
          {
            padding: "16px 24px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            borderBottom: expanded
              ? `1px solid ${theme.branding.gray[200]} `
              : "1px solid transparent",
            transition: "border-bottom 200ms ease-in-out 50ms",
          },
          (theme) =>
            theme.applyStyles({
              borderColor: theme.branding.darkBlue[600],
            }),
        ]}
      >
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Grid
            container
            direction="column"
            xs={10}
            sx={{ minWidth: 0, alignItems: "center" }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.branding.cyan[600], textAlign: "left" }}
            >
              {supertitle}
            </Typography>
            <Grid container sx={{ textAlign: "left" }}>
              {typeof title === "string" ? (
                <Typography variant="subtitle1">{title}</Typography>
              ) : (
                title
              )}
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{
              alignItems: "center",
              transition: "all 0.5s ease-out",
              gap: "16px",
            }}
          >
            {!!headerChipText && headerChipState && (
              <Grid
                container
                direction="row"
                wrap="nowrap"
                sx={[
                  {
                    padding: "2px 12px",
                    borderRadius: "16px",
                    minWidth: "55px",
                    justifyContent: "center",
                    alignItems: "center",
                  },
                  getHeaderChipColors(),
                ]}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "inherit", fontWeight: 500 }}
                >
                  {headerChipText}
                </Typography>
              </Grid>
            )}
            {expanded ? (
              <KeyboardArrowUpIcon
                sx={[
                  {
                    color: customArrowColor || theme.branding.gray[300],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      color:
                        customArrowColorDark || theme.branding.darkBlue[300],
                    }),
                ]}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={[
                  {
                    color: customArrowColor || theme.branding.gray[300],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      color: customArrowColor || theme.branding.darkBlue[300],
                    }),
                ]}
              />
            )}
          </Grid>
        </Grid>
      </ButtonBase>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid
          container
          direction="column"
          sx={{
            width: "100%",
          }}
        >
          <Grid
            container
            sx={{
              overflowY: "scroll",
              padding: isMobile ? "8px 24px" : "16px 24px 36px",
              maxHeight: isMobile ? "316px" : "450px",
            }}
          >
            {children}
          </Grid>
          {cta && (
            <Grid
              container
              sx={{
                bottom: 0,
                zIndex: 5,
                borderTop: `1px solid ${theme.branding.gray[400]}`,
                padding: "12px 24px",
              }}
            >
              {cta}
            </Grid>
          )}
        </Grid>
      </Collapse>
    </Grid>
  );
};

export default CollapsableCard;
